import { Root as VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { Link } from '@tanstack/react-router'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import LessonCancelIcon from '@/assets/icons/lesson-cancel.svg?react'
import LessonCoverIcon from '@/assets/icons/lesson-cover.svg?react'
import Tooltip from '@/components/Tooltip/Tooltip'
import { formatDate, formatTime } from '@/utils/format-date'
import { joinArray } from '@/utils/join-array'

import styles from './LessonEvent.module.scss'

export type LessonEventProps = {
  id: string
  startDate: Date
  endDate: Date
  teacherCoverName?: string
  coTeacherCoverName?: string
  coTeacherName?: string
  course: {
    id: string
    displayName: string
  }
  groupId?: string
  teacherName: string
  classNamesText: string
  roomName?: string
  isCancelled: boolean
  isPreview?: boolean
  clickable?: boolean
}

const LessonEvent = (props: LessonEventProps) => {
  const { t } = useTranslation('timetable')

  const timeText = formatTime(props.startDate) + '-' + formatTime(props.endDate)
  const isLessonCovered = !!props.teacherCoverName || !!props.coTeacherCoverName
  const isLessonCancelled = !!props.isCancelled

  return (
    <Link
      to="/timetable/$courseId/$groupId/lesson-details"
      disabled={!props.clickable || props.isPreview || !props.groupId}
      params={{
        courseId: props.course.id,
        // link is disabled if there is no groupId
        groupId: props.groupId ?? ''
      }}
      search={{ lessonId: props.id }}
      className={clsx(
        styles.event,
        isLessonCovered && styles.eventCover,
        props.isPreview && styles.eventPreview,
        props.isCancelled && styles.lessonCancelled,
        props.clickable && styles.clickable
      )}
      data-test-id={isLessonCancelled ? 'lesson-cancelled' : undefined}
    >
      <VisuallyHidden>{formatDate(props.startDate)}</VisuallyHidden>
      <div className={styles.eventHeader} data-test-id="class-name">
        <span className={styles.eventCourse}>{props.course.displayName}</span>
        {isLessonCovered ? (
          <Tooltip
            text={t('help.covered-lesson')}
            renderTriggerAsChild
            trigger={
              <span>
                <LessonCoverIcon className={styles.coverIcon} />
              </span>
            }
          />
        ) : isLessonCancelled ? (
          <Tooltip
            text={t('help.cancelled-lesson')}
            renderTriggerAsChild
            trigger={
              <span>
                <LessonCancelIcon className={styles.cancelIcon} />
              </span>
            }
          />
        ) : null}
      </div>

      <div data-test-id="lesson-info" className={styles.eventInner}>
        <div className={styles.infoBlock}>
          <div>
            <VisuallyHidden>{t('label.teacher')}</VisuallyHidden>
            <span
              className={clsx(
                styles.eventTeacher,
                props.teacherCoverName && styles.covered
              )}
            >
              {props.teacherName}
            </span>
          </div>

          <div className={styles.coTeacher}>
            <span
              className={clsx(
                styles.eventText,
                props.coTeacherCoverName && styles.covered
              )}
            >
              {props.coTeacherName}
            </span>
          </div>
        </div>

        <div className={styles.infoBlock}>
          <span className={styles.eventText}>{props.classNamesText}</span>
          <div className={styles.timeAndRoom}>
            <span className={styles.eventText}>{timeText}</span>
            {props.roomName ? (
              <span className={styles.eventText}>
                {`${t('text.room')}: `}
                <span data-test-id="room-info">{props.roomName}</span>
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {isLessonCovered ? (
        <span className={styles.eventText} data-test-id="cover-lesson">
          {`${t('text.covered-by')}: ${joinArray([props.teacherCoverName, props.coTeacherCoverName])}`}
        </span>
      ) : null}
    </Link>
  )
}
export default LessonEvent
